import { h } from 'preact';
import {
	useContext, useReducer, createContext, useMemo,
} from 'preact/compat';

/**
 * Defined donation steps.
 */
export enum DonationStepEnum {
	ENTRY = 'entry',
	CHECKOUT = 'checkout',
	CONFIRM = 'confirm',
	SUCCESS = 'success',
}

type StepActionPayload = {
	type: 'MANUAL_STEP',
	payload: {
		step: DonationStepEnum,
	}
}

export type StepAction = (action: StepActionPayload) => void;

/**
 * Donation step reducer.
 */
const stepReducer = (state: DonationStepEnum, action: StepActionPayload) => {
	switch (action.type) {
	case 'MANUAL_STEP':
		return action.payload.step;
	default:
		return state;
	}
};

export const DonationStep = createContext<{
	state: DonationStepEnum,
	dispatch: StepAction
} | undefined>(undefined);

/**
 * Step provider.
 */
const DonationStepProvider = ({ children }: { children: h.JSX.Element }) => {
	const [state, dispatch] = useReducer(stepReducer, DonationStepEnum.ENTRY);

	const stepContext = useMemo(() => ({
		state,
		dispatch,
	}), [state]);

	return (
		<DonationStep.Provider value={stepContext}>
			{children}
		</DonationStep.Provider>
	);
};

/**
 * Donation step hook.
 */
const useDonationStep = () => {
	const context = useContext(DonationStep);
	if (context === undefined) {
		throw new Error('useDonationStep must be used within a DonationStep.Provider');
	}
	return context;
};

export { DonationStepProvider, useDonationStep };
